import React from 'react';

import { useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	StatusIndicator,
	type LoadingStatus,
} from '@atlassian/rovo-platform-ui-components/primitives/LoadingStatusIndicator';
import { ShimmerText } from '@atlassian/rovo-platform-ui-components/primitives/ShimmerText';

import type { Conversation } from '../../types';
import { getConversationListRowLoadingStatus } from '../../utils/conversation-task-status-indicator';

import messages from './messages';

const styles = cssMap({
	shimmerTraceWrapper: {
		flex: '1',
		minWidth: '0px',
	},
	notificationDot: {
		width: '8px',
		height: '8px',
		backgroundColor: token('color.background.selected.bold'),
		borderRadius: token('radius.full'),
	},
	dotSmall: {
		width: '6px',
		height: '6px',
	},
});

function statusToFooterMessage(status: LoadingStatus): MessageDescriptor | null {
	switch (status) {
		case 'loading':
			return messages.rovoIsThinking;
		case 'info':
			return messages.awaitingUserResponse;
		case 'error':
			return messages.errorStatusLabel;
		case 'success':
			return messages.successStatusLabel;
		default:
			return null;
	}
}

/** True when the footer should show task status copy (Done / FAILED / awaiting) beside the optional icon. */
export function shouldShowConversationTaskStatusFooter(conversation: Conversation): boolean {
	const status = getConversationListRowLoadingStatus(
		conversation,
		conversation.readStatus === false,
	);
	return status && status !== 'loading' ? statusToFooterMessage(status) !== null : false;
}

/** Status chip used in row `elemAfter` / controls: spinner, status icon, or unread dot. */
export function ConversationListRowStatusChip({
	conversation,
	showUnreadDot = true,
	testId,
}: {
	conversation: Conversation;
	showUnreadDot?: boolean;
	testId?: string;
}): JSX.Element | null {
	const isUnread = conversation.readStatus === false;
	const status = getConversationListRowLoadingStatus(conversation, isUnread);

	if (status) return <StatusIndicator status={status} size="small" testId={testId} />;
	if (isUnread && showUnreadDot)
		return <Box xcss={cx(styles.notificationDot, styles.dotSmall)} testId={testId} />;
	return null;
}

export type ConversationStatusLabelProps = {
	conversation: Conversation;
	/** When false, only the text line is rendered (icon is omitted). Default true. */
	showStatusIcon?: boolean;
};

/**
 * Footer row fragment: optional {@link StatusIndicator}-aligned icon + Done / FAILED / awaiting copy
 * (shimmer when awaiting). Renders nothing when no footer label applies for this task/channel state.
 */
export function ConversationStatusLabel({
	conversation,
	showStatusIcon = false,
}: ConversationStatusLabelProps): JSX.Element | null {
	const { formatMessage } = useIntl();
	const status = getConversationListRowLoadingStatus(
		conversation,
		conversation.readStatus === false,
	);

	const labelDescriptor = status ? statusToFooterMessage(status) : null;
	const isShimmering = status === 'loading' || status === 'info';

	if (!labelDescriptor) return null;

	return (
		<Inline alignBlock="center" space="space.050">
			{showStatusIcon && (
				<ConversationListRowStatusChip conversation={conversation} showUnreadDot={false} />
			)}

			{isShimmering ? (
				<Box xcss={styles.shimmerTraceWrapper}>
					<ShimmerText size="small" truncate>
						{formatMessage(labelDescriptor)}
					</ShimmerText>
				</Box>
			) : (
				<Text size="small" weight="regular" maxLines={1} align="start" color="color.text.subtle">
					{formatMessage(labelDescriptor)}
				</Text>
			)}
		</Inline>
	);
}
